export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateEmail = (email: string): ValidationResult => {
  const trimmedEmail = email.trim();

  if (!trimmedEmail) {
    return { isValid: true };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Invalid email format'
    };
  }

  if (trimmedEmail.length > 255) {
    return {
      isValid: false,
      error: 'Email is too long'
    };
  }

  return { isValid: true };
};

/**
 * Determines if a string looks like a phone number (no @ symbol)
 */
export const isPhoneNumber = (input: string): boolean => {
  const trimmed = input.trim();
  return trimmed.length > 0 && !trimmed.includes('@');
};

/**
 * Cleans a phone number by removing all non-numeric characters except leading +
 * Ensures the phone number is 100% numeric with no spaces
 */
export const cleanPhoneNumber = (phoneNumber: string): string => {
  const trimmed = phoneNumber.trim();

  // Check if it starts with +
  const hasPlus = trimmed.startsWith('+');

  // Remove all non-numeric characters
  const digitsOnly = trimmed.replace(/\D/g, '');

  // Return with + prefix if it had one
  return hasPlus ? `+${digitsOnly}` : digitsOnly;
};

/**
 * Validates a phone number ensuring it's 100% numeric (with optional + prefix)
 * NO spaces, dashes, or other characters allowed
 */
export const validatePhoneNumber = (phoneNumber: string): ValidationResult => {
  const trimmedPhone = phoneNumber.trim();

  if (!trimmedPhone) {
    return { isValid: true };
  }

  // Check for any spaces
  if (trimmedPhone.includes(' ')) {
    return {
      isValid: false,
      error: 'Phone number cannot contain spaces'
    };
  }

  // Only allow + at the start, followed by digits
  const strictPhoneRegex = /^\+?[0-9]+$/;

  if (!strictPhoneRegex.test(trimmedPhone)) {
    return {
      isValid: false,
      error: 'Phone number must be numeric only (with optional + prefix)'
    };
  }

  // Extract digits only for length check
  const digitsOnly = trimmedPhone.replace(/\D/g, '');
  if (digitsOnly.length < 6 || digitsOnly.length > 15) {
    return {
      isValid: false,
      error: 'Phone number must be between 6 and 15 digits'
    };
  }

  return { isValid: true };
};
